import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './shared/LanguageToggle';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const isEnglish = i18n.language === 'en';
  const prefix = isEnglish ? '/en' : '';

  const navLinks: Array<{ to: string; label: string; exact?: boolean; highlight?: boolean }> = [
    { to: `${prefix}/`, label: t('nav.home'), exact: true },
    { to: isEnglish ? `${prefix}/services` : `${prefix}/servicios`, label: t('nav.services') },
    { to: isEnglish ? `${prefix}/about` : `${prefix}/nosotros`, label: t('nav.about') },
    { to: `${prefix}/faq`, label: 'FAQ' },
    { to: isEnglish ? `${prefix}/contact` : `${prefix}/contacto`, label: t('nav.contact') },
    // NOTE: the in-progress insurance forms stay parked under /wip (routes in App.tsx),
    // reachable only by direct URL — no nav link. To launch publicly, move the routes to
    // /formularios and add a nav link here. The `highlight` styling below is kept so a
    // future highlighted link still works.
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path || location.pathname === path.replace(/\/$/, '');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to={`${prefix}/`} className="flex items-center">
            <img
              src="/images/logo/full_color_nobg.png"
              alt="Confidenza Seguros"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={
                  link.highlight
                    ? 'px-2 py-0.5 rounded border border-red-600 text-red-600 text-sm font-bold uppercase tracking-wide hover:bg-red-600 hover:text-white transition-colors'
                    : `text-gray-700 hover:text-primary-600 transition-colors ${
                        isActive(link.to, link.exact) ? 'text-primary-600 font-medium' : ''
                      }`
                }
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Language Toggle + CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageToggle />
            <Link
              to={isEnglish ? `${prefix}/contact` : `${prefix}/contacto`}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('nav.getQuote')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-3">
            <LanguageToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-primary-600 focus:outline-none focus:text-primary-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={
                    link.highlight
                      ? 'block px-3 py-2 text-red-600 font-bold uppercase tracking-wide'
                      : `block px-3 py-2 text-gray-700 hover:text-primary-600 ${
                          isActive(link.to, link.exact) ? 'text-primary-600 font-medium' : ''
                        }`
                  }
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-3 py-2">
                <Link
                  to={isEnglish ? `${prefix}/contact` : `${prefix}/contacto`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors text-center"
                >
                  {t('nav.getQuote')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
